import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "@bd/ui-kit";
import { t } from "../i18n";

/** A rating out of five turned into a word and a colour, so a fit never needs a legend. */
export function fitLabel(n: number | undefined): { word: string; color: string } {
  if (n === undefined) return { word: t("Not tried"), color: "var(--muted)" };
  if (n >= 5) return { word: t("Great"), color: "var(--green)" };
  if (n >= 4) return { word: t("Good"), color: "var(--green)" };
  if (n >= 3) return { word: t("OK"), color: "var(--warn)" };
  return { word: t("Weak"), color: "var(--bad)" };
}

export function FitPill({ n, title }: { n: number | undefined; title?: string }) {
  const f = fitLabel(n);
  return (
    <span className="fit-pill" style={{ color: f.color, borderColor: f.color }} title={title}>
      {f.word}
    </span>
  );
}

/** A clock (ui-kit has no time icon yet): play time and minutes. */
export function ClockIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "none" }}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** One fact as an icon and a short value ("clock 60 min", "coin $46"); what it means lives in the tooltip. */
export function Fact({ icon, children, title, tone }: { icon: string; children: ReactNode; title?: string; tone?: "warn" | "bad" | "good" }) {
  return (
    <span className={`fact${tone ? ` ${tone === "warn" ? "warnc" : tone}` : ""}`} title={title}>
      {icon === "clock" ? <ClockIcon /> : <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

export interface PickOption {
  id: string;
  name: string;
  meta?: ReactNode; // the facts to choose by, as icon + value on one line
  hint?: string; // the explanation, as a tooltip
  fit?: number; // 1..5 if known
  fitTitle?: string; // what the fit is measured against ("with Set collection")
  tag?: string; // e.g. "Trending"
  locked?: string; // why it's locked ("Arrives 1985", "Research it")
}

/** One concept choice (mechanic, theme, audience, format or size) as a card that shows the pick and how well it fits. */
export function PickCard({ label, value, meta, fit, fitTitle, onOpen, disabled, title, testId }: { label: string; value: string; meta?: ReactNode; fit?: number; fitTitle?: string; onOpen: () => void; disabled?: boolean; title?: string; testId: string }) {
  return (
    <button className="card pick-card lift" onClick={onOpen} disabled={disabled} data-testid={testId} title={title} aria-label={t("{label}: {value}. Change", { label, value })}>
      <span className="caps">{label}</span>
      <span className="pick-value">{value}</span>
      {(fitTitle || meta) && (
        <span className="facts">
          {fitTitle && <FitPill n={fit} title={fitTitle} />}
          {meta}
        </span>
      )}
      {!disabled && <Icon name="chevron" size={16} color="var(--muted)" />}
    </button>
  );
}

/**
 * The full list for one choice: searchable, best fits first, locked items tucked away at the end.
 * One decision at a time, with the facts to pick by on each row.
 */
export function Picker({ title, fitLabelText, options, value, onPick, onClose, testId }: { title: string; fitLabelText?: string; options: PickOption[]; value: string; onPick: (id: string) => void; onClose: () => void; testId: string }) {
  const [q, setQ] = useState("");
  const [showLocked, setShowLocked] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // the search box is only worth focusing on a keyboard: on phones it would pop the keyboard up
    if (window.matchMedia("(pointer: fine)").matches) input.current?.focus();
  }, []);
  const match = (o: PickOption) => !q || o.name.toLowerCase().includes(q.toLowerCase());
  const open = useMemo(() => options.filter((o) => !o.locked && match(o)).sort((a, b) => (b.fit ?? 2.5) - (a.fit ?? 2.5) || a.name.localeCompare(b.name)), [options, q]);
  const locked = useMemo(() => options.filter((o) => o.locked && match(o)), [options, q]);
  const many = options.length > 10;
  return (
    <div className="picker stack" data-testid={testId}>
      <div className="picker-head">
        <button className="icon-btn" onClick={onClose} data-testid="picker-back" aria-label={t("Back")} title={t("Back")}><Icon name="back" size={20} /></button>
        <strong style={{ fontSize: "1.1rem" }} title={fitLabelText}>{title}</strong>
      </div>
      {many && <input ref={input} className="picker-search" placeholder={t("Search")} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t("Search: {list}", { list: title })} onKeyDown={(e) => e.key === "Enter" && open[0] && onPick(open[0].id)} />}
      <div className="picker-list">
        {open.map((o) => (
          <button key={o.id} className={`picker-row ${o.id === value ? "on" : ""}`} onClick={() => onPick(o.id)} title={o.hint} data-testid={`option-${o.id}`}>
            <span style={{ minWidth: 0 }}>
              <span className="picker-name">{o.name}{o.tag && <span className="badge" style={{ marginLeft: 8 }}><Icon name="trend" size={11} color="#fff" /> {o.tag}</span>}</span>
              {o.meta && <span className="facts muted">{o.meta}</span>}
            </span>
            {o.fitTitle !== undefined ? <FitPill n={o.fit} title={o.fitTitle} /> : <span />}
          </button>
        ))}
        {open.length === 0 && <span className="muted" style={{ padding: 8 }}>{t("Nothing matches.")}</span>}
        {locked.length > 0 && (
          <button className="btn small ghost" style={{ alignSelf: "flex-start", marginTop: 6 }} onClick={() => setShowLocked((v) => !v)} aria-label={showLocked ? t("Hide locked") : t("Show locked ({n})", { n: locked.length })} title={showLocked ? t("Hide locked") : t("Show locked ({n})", { n: locked.length })} data-testid="picker-locked">
            <Icon name="lock" size={14} /> {locked.length} <Icon name="chevron" size={14} style={{ transform: showLocked ? "rotate(-90deg)" : "rotate(90deg)" }} />
          </button>
        )}
        {showLocked && locked.map((o) => (
          <div key={o.id} className="picker-row locked" aria-disabled="true">
            <span className="picker-name">{o.name}</span>
            <Fact icon="lock">{o.locked}</Fact>
          </div>
        ))}
      </div>
    </div>
  );
}
